import React, { useState, useEffect } from 'react';
import {
  Box, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tooltip
} from '@mui/material';
import supabase from '../../../supabaseClient';
import AttendanceSummaryBar from './AttendanceSummaryBar';
import Last12MonthsReport from './Last12MonthsReport';
import ReportPeriodPicker from './ReportPeriodPicker';
import ReportWorkerPicker from './ReportWorkerPicker';
import EmployeeRow from './EmployeeRow';
import { applyRoundingRules, findMatchingRule } from '../../../Utils/roundingRules';

const pad = n => String(n).padStart(2, '0');
const localDateStr = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const fmtTime = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

function AttendanceReportComponent({ idConfig }) {
  const today = new Date();
  const firstDay = localDateStr(new Date(today.getFullYear(), today.getMonth(), 1));
  const lastDay  = localDateStr(new Date(today.getFullYear(), today.getMonth() + 1, 0));

  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo]     = useState(lastDay);
  const [periodMode, setPeriodMode] = useState('month');
  const [employees, setEmployees]   = useState([]);
  const [rules, setRules]           = useState([]);
  const [summaryPaid, setSummaryPaid] = useState(0);
  const [summaryRemaining, setSummaryRemaining] = useState(0);
  const [loading, setLoading]   = useState(false);
  const [paymentTick, setPaymentTick] = useState(0);
  const [lifetimeByEmployee, setLifetimeByEmployee] = useState({});
  const lifetimeTotal = Object.values(lifetimeByEmployee).reduce((a, v) => a + v, 0);

  const [workerFilter, setWorkerFilter] = useState('all');

  const [roundingConfig, setRoundingConfig] = useState({
    checkIn:  { enabled: false, thresholdMinutes: 12, direction: 'down' },
    checkOut: { enabled: false, thresholdMinutes: 12, direction: 'up'   },
  });

  // Simple manual rounding (fallback when no DB rule applies)
  const roundTimestamp = (dateStr, cfg) => {
    const d = new Date(dateStr);
    if (!cfg.enabled) return d;
    const rem = d.getMinutes() + d.getSeconds() / 60;
    if (cfg.direction === 'up'   && 60 - rem <= cfg.thresholdMinutes) d.setHours(d.getHours() + 1, 0, 0, 0);
    if (cfg.direction === 'down' && rem       <= cfg.thresholdMinutes) d.setMinutes(0, 0, 0);
    return d;
  };

  const calcShiftDuration = (r) => {
    const matchedRule = findMatchingRule(r.check_in, rules);
    if (matchedRule) {
      const { billableMinutes } = applyRoundingRules(r.check_in, r.check_out, matchedRule);
      return billableMinutes;
    }
    // fall back to manual config
    const ci = roundTimestamp(r.check_in,  roundingConfig.checkIn);
    const co = roundTimestamp(r.check_out, roundingConfig.checkOut);
    return Math.max(0, (co - ci) / 60000);
  };

  const getShiftTooltip = (r) => {
    const matchedRule = findMatchingRule(r.check_in, rules);
    const ciRaw = new Date(r.check_in);
    const coRaw = new Date(r.check_out);
    if (matchedRule) {
      const { billableStart, billableEnd } = applyRoundingRules(r.check_in, r.check_out, matchedRule);
      return `Surowy: ${fmtTime(ciRaw)}–${fmtTime(coRaw)} → Po zaokrągleniu: ${fmtTime(billableStart)}–${fmtTime(billableEnd)} (reguła: ${matchedRule.name})`;
    }
    return `Surowy: ${fmtTime(ciRaw)}–${fmtTime(coRaw)}`;
  };

  const fetchRules = async () => {
    if (!idConfig) return;
    const { data, error } = await supabase
      .from('rounding_rules')
      .select('*')
      .eq('id_configuration', idConfig)
      .eq('is_active', true);
    if (!error) setRules(data || []);
  };

  const fetchReport = async () => {
    if (!idConfig) return;
    setLoading(true);

    const { data: attendanceData, error } = await supabase
      .from('attendance')
      .select('check_in, check_out, employee_id, employees(id, name, surname, hourly_rate, isDriver)')
      .eq('id_configuration', idConfig)
      .gte('check_in', `${dateFrom}T00:00:00`)
      .lte('check_in', `${dateTo}T23:59:59`)
      .not('check_in', 'is', null)
      .not('check_out', 'is', null);

    if (error) { console.log(error); setLoading(false); return; }

    const map = {};
    (attendanceData || []).forEach(r => {
      const emp = r.employees;
      if (!emp) return;
      if (!map[emp.id]) map[emp.id] = { ...emp, records: [] };
      map[emp.id].records.push(r);
    });
    setEmployees(Object.values(map).filter(emp => workerFilter === 'all' || workerFilter === 'drivers' && emp.isDriver || workerFilter === 'hall_workers' && !emp.isDriver));

    const { data: paymentsData } = await supabase
      .from('payments')
      .select('amount, employee_id')
      .eq('id_configuration', idConfig)
      .gte('date', dateFrom)
      .lte('date', dateTo);

    const paidByEmployee = {};
    (paymentsData || []).forEach(p => {
      paidByEmployee[p.employee_id] = (paidByEmployee[p.employee_id] || 0) + p.amount;
    });

    let totalPaidSum = 0;
    let totalRemainingSum = 0;
    Object.values(map).forEach(emp => {
      const mins = emp.records.reduce((a, r) => {
        if (!r.check_in || !r.check_out) return a;
        return a + calcShiftDuration(r);
      }, 0);
      const salary = (mins / 60) * (emp.hourly_rate || 0);
      const paid = paidByEmployee[emp.id] || 0;
      totalPaidSum += paid;
      totalRemainingSum += Math.max(0, salary - paid);
    });
    setSummaryPaid(totalPaidSum);
    setSummaryRemaining(totalRemainingSum);
    setLoading(false);
  };

  useEffect(() => { fetchRules(); }, [idConfig]);
  useEffect(() => { fetchReport(); }, [idConfig, dateFrom, dateTo, paymentTick, roundingConfig, rules, workerFilter]);

  const totalMinutes = employees.reduce((acc, emp) =>
    acc + emp.records.reduce((a, r) => {
      if (!r.check_in || !r.check_out) return a;
      return a + calcShiftDuration(r);
    }, 0), 0);

  const totalSalary = employees.reduce((acc, emp) => {
    const mins = emp.records.reduce((a, r) => {
      if (!r.check_in || !r.check_out) return a;
      return a + calcShiftDuration(r);
    }, 0);
    return acc + (mins / 60) * (emp.hourly_rate || 0);
  }, 0);

  return (
    <Box>
      <Last12MonthsReport
        idConfig={idConfig}
      />

      <ReportWorkerPicker
        workerFilter={workerFilter}
        setWorkerFilter={setWorkerFilter}
        />

      <ReportPeriodPicker
        dateFrom={dateFrom}
        dateTo={dateTo}
        periodMode={periodMode}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onPeriodModeChange={setPeriodMode}
      />

      {!loading && employees.length > 0 && (
        <AttendanceSummaryBar
          totalMinutes={totalMinutes}
          totalSalary={totalSalary}
          totalPaid={summaryPaid}
          remaining={summaryRemaining}
          lifetimeRemaining={lifetimeTotal}
        />
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Pracownik</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Godziny pracy</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Urlopy</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Nadgodziny</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Stawka/h</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Suma</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Wypłacono</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Do wypłaty za dany okres</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Suma do wypłaty</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Reguła</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    Brak danych dla wybranego okresu
                  </TableCell>
                </TableRow>
              ) : (
                employees.map(emp => (
                  <EmployeeRow
                    key={emp.id}
                    employee={emp}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    idConfig={idConfig}
                    roundingConfig={roundingConfig}
                    rules={rules}
                    getShiftTooltip={getShiftTooltip}
                    onPaymentAdded={() => setPaymentTick(t => t + 1)}
                    onLifetimeChange={(id, val) => setLifetimeByEmployee(prev => ({ ...prev, [id]: val }))}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default AttendanceReportComponent;
